<script setup>
import { computed, useTemplateRef, ref } from 'vue';
import { useElementSize } from '@vueuse/core';
import { REPLY_EDITOR_MODES } from './constants';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  mode: {
    type: String,
    default: REPLY_EDITOR_MODES.REPLY,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isReplyRestricted: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['toggleMode']);

const wootEditorReplyMode = useTemplateRef('wootEditorReplyMode');
const wootEditorPrivateMode = useTemplateRef('wootEditorPrivateMode');

const replyModeSize = useElementSize(wootEditorReplyMode);
const privateModeSize = useElementSize(wootEditorPrivateMode);

const helpDialogRef = ref(null);

/**
 * Computed boolean indicating if the editor is in private note mode
 * When isReplyRestricted is true, force switch to private note
 * Otherwise, respect the current mode prop
 * @type {ComputedRef<boolean>}
 */
const isPrivate = computed(() => {
  if (props.isReplyRestricted) {
    // Force switch to private note when replies are restricted
    return true;
  }
  // Otherwise respect the current mode
  return props.mode === REPLY_EDITOR_MODES.NOTE;
});

/**
 * Computes the width of the sliding background chip in pixels
 * Includes 16px of padding in the calculation
 * @type {ComputedRef<string>}
 */
const width = computed(() => {
  const widthToUse = isPrivate.value
    ? privateModeSize.width.value
    : replyModeSize.width.value;

  const widthWithPadding = widthToUse + 16;
  return `${widthWithPadding}px`;
});

/**
 * Computes the X translation value for the sliding background chip
 * Translates by the width of reply mode + padding when in private mode
 * @type {ComputedRef<string>}
 */
const translateValue = computed(() => {
  const xTranslate = isPrivate.value ? replyModeSize.width.value + 16 : 0;

  return `${xTranslate}px`;
});

const openHelpDialog = () => {
  helpDialogRef.value?.open();
};
</script>

<template>
  <div class="flex items-center gap-2">
    <button
      class="flex items-center w-auto h-8 p-1 transition-all border rounded-full bg-n-alpha-2 group relative duration-300 ease-in-out z-0 active:scale-[0.995] active:duration-75"
      :disabled="disabled || isReplyRestricted"
      :class="{
        'cursor-not-allowed': disabled || isReplyRestricted,
      }"
      @click="$emit('toggleMode')"
    >
      <div ref="wootEditorReplyMode" class="flex items-center gap-1 px-2 z-20">
        {{ $t('CONVERSATION.REPLYBOX.REPLY') }}
      </div>
      <div
        ref="wootEditorPrivateMode"
        class="flex items-center gap-1 px-2 z-20"
      >
        {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE') }}
      </div>
      <div
        class="absolute shadow-sm rounded-full h-6 w-[var(--chip-width)] ease-in-out translate-x-[var(--translate-x)] rtl:translate-x-[var(--rtl-translate-x)] bg-n-solid-1"
        :class="{
          'transition-all duration-300': !disabled && !isReplyRestricted,
        }"
        :style="{
          '--chip-width': width,
          '--translate-x': translateValue,
          '--rtl-translate-x': `calc(-1 * var(--translate-x))`,
        }"
      />
    </button>
    <!-- 私人便笺帮助入口 -->
    <button
      v-if="isPrivate"
      type="button"
      class="flex items-center gap-1 px-2 py-1 text-xs text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 rounded-md transition-colors"
      @click="openHelpDialog"
    >
      <Icon icon="i-lucide-help-circle" class="size-3.5" />
      <span>{{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE_HELP') }}</span>
    </button>

    <!-- 私人便笺帮助弹窗 -->
    <Dialog
      ref="helpDialogRef"
      type="edit"
      :title="$t('CONVERSATION.REPLYBOX.PRIVATE_NOTE_HELP_TITLE')"
      :show-cancel-button="false"
      :show-confirm-button="true"
      :confirm-button-label="$t('DIALOG.BUTTONS.CLOSE')"
      width="2xl"
      @confirm="helpDialogRef?.close()"
    >
      <div
        class="text-sm text-n-slate-11 space-y-4 max-h-[60vh] overflow-y-auto"
      >
        <p class="text-n-slate-12 font-medium whitespace-pre-line">
          {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE_HELP_INTRO') }}
        </p>

        <div class="space-y-3">
          <!-- 场景1 -->
          <div class="p-3 bg-n-alpha-1 rounded-lg">
            <h4 class="font-medium text-n-slate-12 mb-1">
              {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE_SCENARIO_1_TITLE') }}
            </h4>
            <p class="text-n-slate-11 whitespace-pre-line">
              {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE_SCENARIO_1_DESC') }}
            </p>
          </div>

          <!-- 场景2 -->
          <div class="p-3 bg-n-alpha-1 rounded-lg">
            <h4 class="font-medium text-n-slate-12 mb-1">
              {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE_SCENARIO_2_TITLE') }}
            </h4>
            <p class="text-n-slate-11 whitespace-pre-line">
              {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE_SCENARIO_2_DESC') }}
            </p>
          </div>

          <!-- 场景3 -->
          <div class="p-3 bg-n-alpha-1 rounded-lg">
            <h4 class="font-medium text-n-slate-12 mb-1">
              {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE_SCENARIO_3_TITLE') }}
            </h4>
            <p class="text-n-slate-11 whitespace-pre-line">
              {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE_SCENARIO_3_DESC') }}
            </p>
          </div>

          <!-- 场景4 -->
          <div class="p-3 bg-n-alpha-1 rounded-lg">
            <h4 class="font-medium text-n-slate-12 mb-1">
              {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE_SCENARIO_4_TITLE') }}
            </h4>
            <p class="text-n-slate-11 whitespace-pre-line">
              {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE_SCENARIO_4_DESC') }}
            </p>
          </div>

          <!-- 场景5 -->
          <div class="p-3 bg-n-alpha-1 rounded-lg">
            <h4 class="font-medium text-n-slate-12 mb-1">
              {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE_SCENARIO_5_TITLE') }}
            </h4>
            <p class="text-n-slate-11 whitespace-pre-line">
              {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE_SCENARIO_5_DESC') }}
            </p>
          </div>

          <!-- 场景6 -->
          <div class="p-3 bg-n-alpha-1 rounded-lg">
            <h4 class="font-medium text-n-slate-12 mb-1">
              {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE_SCENARIO_6_TITLE') }}
            </h4>
            <p class="text-n-slate-11 whitespace-pre-line">
              {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE_SCENARIO_6_DESC') }}
            </p>
          </div>
        </div>
      </div>
    </Dialog>
  </div>
</template>
